import { MissionControlNodeProps } from '@components/missionControl/dataflow/MissionControlNode.component';
import { useAnalyses } from '@models/analysis';
import BillingAccountORM from '@models/billingAccount';
import BuildOrchestrationORM, { BuildOrchestration } from '@models/buildOrchestration';
import { DashboardORM, useDashboards } from '@models/dashboard';
import { ContextItem, ContextORM } from '@models/context';
import DataMartORM, { DataMart, ReportColumn } from '@models/dataMart';
import DataLoadORM from '@models/dataload';
import FileORM from '@models/file';
import PipelineORM from '@models/pipeline';
import { NodeFieldJoinPath, PipelineNode, PipelineNodeORM, PipelineNodeRelationship, PipelineNodeRelationshipORM } from '@models/pipelineNode';
import { PipelineOrchestration } from '@models/pipelineOrchestration';
import { ColumnStats, TableLoadResponse } from '@models/shared';
import SourceORM, { ColumnPreference, Source, SourceDataConnection, SourceDataConnectionORM, SourceDataRelationship, SourceDataRelationshipORM, SourceRecordType, SourceRecordTypeORM, SourceType } from '@models/source';
import { Template } from '@models/template';
import TemplateGroupORM, { TemplateGroup } from '@models/templateGroup';
import { closeWaiting, confirmation, showWaiting } from '@services/alert/alert.service';
import ApiService, { ListRecordsResponse, SingleRecordResponse } from '@services/api/api.service';
import ConfigService, { Config, UserConfig } from '@services/config/config.service';
import { useFeatureFlagValue } from '@services/featureFlags/featureFlag.service';
import { parseDateString } from '@services/time.service';
import TrackingService, { Events } from '@services/tracking.service';
import { apiurl } from '@services/url.service';
import { useMemo, useCallback, useEffect } from 'react';
import AuthService, { useAuthState } from '@services/auth/auth.service';
import { isReadOnlyUser } from '@components/project/DraftModeRequired.component';

import {
    useQuery,
    QueryClient,
} from 'react-query';

import { Viewport } from 'reactflow';

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false, // default: true
        },
    },
});

export interface BusinessObjectLineageNode {
    id: string;
    record_type: string;
    record_uuid: string;
    record_source_record_type_id?: string;
    record_business_object_id?: string;
    record_data: {
        [key: string]: any;
    }
}

export interface BusinessObjectLineageEdge {
    from_id: string;
    to_id: string;
}

export interface RecordDetailsResponse {
    data: {
        [key: string]: any
    };
    lineage: {
        nodes: BusinessObjectLineageNode[];
        edges: BusinessObjectLineageEdge[];
    }
}

export interface RelationshipGraphResponse {
    nodes: BusinessObjectLineageNode[],
    edges: BusinessObjectLineageEdge[],
}


export const useProjectConfig = () => {
    return useQuery('project_config', async () => {
        await ConfigService.getInstance().loadConfig();
        return ConfigService.getInstance().config!;
    }, {
        // 30 mins
        staleTime: 60 * 30 * 1000
    });
}

export const useUserConfig = () => {
    return useQuery('user_config', async () => {
        await ConfigService.getInstance().loadUserConfig();
        return ConfigService.getInstance().userConfig;
    }, {
        // 30 mins
        staleTime: 60 * 30 * 1000,
    });
}

export const saveUserConfig = async (userConfig: UserConfig) => {
    await ApiService.getInstance().request('POST', '/user-config', userConfig);
    queryClient.invalidateQueries('user_config');
}

export const enterDraftMode = async (trigger: string, showModal: boolean = true) => {
    const authState = AuthService.getInstance().plbAuthState;
    if (isReadOnlyUser(authState)) {
        if (showModal) {
            confirmation({
                header: 'Read Only User',
                message: 'Read Only users cannot enter Develop Mode. You must always use Production Mode.',
                confirmationButtonText: 'Got it',
                onConfirm: () => {},
                onClose: () => {}
            });
        }
        return;
    }
    
    let showWaitInt: any;
    if (showModal) {
        showWaitInt = setTimeout(() => showWaiting({message: 'Loading Draft...'}), 500); // show waiting if it takes a while
    }
    // we only have a single draft slot - the the id is the same for all right now
    const draft_version_id = 'draft_mode';
    if (draft_version_id) {
        // persist to local storage for page refresh
        localStorage.setItem('draft_version_id', draft_version_id as string);
        queryClient.setQueryData(['draft_version_id'], draft_version_id);
        clearDraftData();
        if (showModal) {
            clearTimeout(showWaitInt);
            closeWaiting();
        }
        
        TrackingService.track_event(Events.TOGGLE_DRAFT_MODE, {
            trigger: trigger,
            enable: 'DRAFT'
        });
    }
}

export const exitDraftMode = async (trigger?: string) => {
    localStorage.removeItem('draft_version_id');
    queryClient.setQueryData(['draft_version_id'], null);
    await clearDraftData();
    if (typeof trigger !== 'undefined') {
        TrackingService.track_event(Events.TOGGLE_DRAFT_MODE, {
            trigger: trigger,
            enable: 'PROD'
        });
    }
}

export const clearDraftData = async () => {
    // invalidate everything except the draft version
    await invalidateEverything();
 }

export const useDraftVersionId = () => {
    // Will get cached draft version user was working on - if this returns a value they are in draft mode.
    return useQuery('draft_version_id', async () => {
        let version = queryClient.getQueryData(['draft_version_id']);
        if (!version) {
            version = localStorage.getItem('draft_version_id');
        }
        return version;
    }, {
        // 30 mins
        staleTime: 60 * 30 * 1000
    });
}

export interface SnowflakeTableOption {
    name: string;
    schema_name: string;
    database_name: string;
    rows: number;
}

export const useAvailableSnowflakeTables = () => {
    const projectConfig = useProjectConfig();
    return useQuery(['snowflake_tables', projectConfig.data ? projectConfig.data.id : 'no-id'], async () => {
        if (!projectConfig.data || projectConfig.data.database != 'SNOWFLAKE') {
            return [];
        }
        const results = await ApiService.getInstance().request('GET', `/tenant-config/${projectConfig.data.id}/get-snowflake-tables`) as ListRecordsResponse<SnowflakeTableOption>;
        return results.records;
    }, {
        // 30 mins since they can click to refresh,
        staleTime: 60 * 1000 * 30,
    })
}


export const useRecord = (businessObjectId: string, recordUuid: string, includeLineage: boolean = true) => {
    return useQuery(['business_object_record', businessObjectId, recordUuid, includeLineage], async () => {
        const result = await ApiService.getInstance().request('GET', `/businessobjects/${businessObjectId}/record/${recordUuid}`, {
            lineage: includeLineage ? 'y' : 'n'
        }) as RecordDetailsResponse;
        return result;
    }, {
        // 2 mins?
        staleTime: 60 * 1000 * 2
    })
}

export const useRelationshipGraph = (businessObjectId: string, recordUuid: string) => {
    return useQuery(['business_object_relationship_graph', businessObjectId, recordUuid], async () => {
        const result = await ApiService.getInstance().request('GET', `/businessobjects/${businessObjectId}/record/${recordUuid}/relationship-graph`) as RelationshipGraphResponse;
        return result; 
    }, {
        // 2 mins?
        staleTime: 60 * 1000 * 2
    })
}

export const useSourceRecord = (sourceId: string, sourceRecordTypeId: string, recordUuid: string) => {
    return useQuery(['source_record', sourceId, sourceRecordTypeId, recordUuid], async () => {
        const result = await ApiService.getInstance().request('GET', `/sources/${sourceId}/record-type/${sourceRecordTypeId}/record/${recordUuid}`) as SingleRecordResponse<any>;

        result.record._LOADED_AT = parseDateString(result.record._LOADED_AT);
        return result.record;
    }, {
        // 30 mins
        staleTime: 60 * 1000 * 30
    });
}


export const useSources = () => {
    return useQuery('sources', async () => {
        const { records } = await SourceORM.find();
        return records;
    }, {
        staleTime: 30 * 1000 // 30 seconds stale time
    });
}

export const useSource = (sourceId: string) => {
    return useQuery(['sources', sourceId], async () => {
        if (!sourceId) {
            return undefined;
        }
        const record = await SourceORM.findById(sourceId);
        return record;
    }, {
        staleTime: 30 * 1000 // 30 seconds stale time
    });
}

export const saveSource = async (source: Source) => {
    const result = await SourceORM.save(source);
    queryClient.invalidateQueries('sources');
    return result;
}

export const deleteSource = async (source: Source) => {
    const result = await SourceORM.delete(source);
    queryClient.invalidateQueries('sources');
    return result;
}


export const useIsInDraftMode = () => {
    const {data: draftVersionId} = useDraftVersionId();
    return !!draftVersionId;
}

export const useDraftModeToggle = () => {
    const {data: draftVersionId} = useDraftVersionId();
    const authState = useAuthState();
    
    return useCallback(async (trigger: string) => {
        if (isReadOnlyUser(authState)) {
            return;
        }
        
        if (draftVersionId) {
            await exitDraftMode(trigger);
        } else {
            await enterDraftMode(trigger);
        }
    }, [draftVersionId, authState]);
}

export const useTemplateName = (templateId: string) => {
    const templates = useTemplates();
    if (!templateId || !templates.data) return null;
    const template = templates.data.find((t: any) => t.id === templateId);
    return template ? template.name : null;
};

export const saveSourceRecordType = async (sourceRecordType: SourceRecordType) => {
    const result = await SourceRecordTypeORM.save(sourceRecordType);
    invalidateSourceRecordTypes();
    invalidateMissionControlDataFlowData();
    invalidateMissionControlDataSourcesData();
    return result;
};

export const deleteSourceRecordType = async (sourceRecordType: SourceRecordType) => {
    const result = await SourceRecordTypeORM.delete(sourceRecordType);
    invalidateSourceRecordTypes();
    invalidateMissionControlDataFlowData();
    invalidateMissionControlDataSourcesData();
}

export const useSourceDataRelationships = () => {
    return useQuery('source_data_relationships', async () => {
        const { records } = await SourceDataRelationshipORM.find();
        return records;
    }, {
        staleTime: 30 * 1000
    });
};

export const invalidateSourceRecordTypes = () => {
    queryClient.invalidateQueries('source_record_types');
    queryClient.invalidateQueries('source_record_type');
}

export const invalidateSources = () => {
    queryClient.invalidateQueries('sources');
}


export const saveSourceDataRelationship = async (rel: SourceDataRelationship) => {
    const result = await SourceDataRelationshipORM.save(rel);
    queryClient.invalidateQueries('source_data_relationships');
    return result;
}

export const deleteSourceDataRelationship = async (rel: SourceDataRelationship) => {
    const result = await SourceDataRelationshipORM.delete(rel);
    queryClient.invalidateQueries('source_data_relationships');
    return result;
}

export const useSourceRecordTypes = (sourceId: string | null = '', enabled: boolean = true) => {
    return useQuery(['source_record_types', sourceId], async () => {
        let filter = {}
        if (!!sourceId) {
            filter = {'source_id': sourceId}
        }
        const { records } = await SourceRecordTypeORM.find(filter);
        return records;
    }, {
        enabled: enabled,
        staleTime: 30 * 1000,  // 30 seconds stale time
    })
}

export const useSourceRecordType = (sourceRecordTypeId: string, enabled: boolean = true) => {
    return useQuery(['source_record_type', sourceRecordTypeId], async () => {
        if (!sourceRecordTypeId) {
            return null;
        }
        return  await SourceRecordTypeORM.findById(sourceRecordTypeId);
    }, {
        enabled,
        staleTime: 30 * 1000,  // 30 seconds stale time
    })
}

export const invalidateSourceRecordType = (sourceRecordTypeId: string) => {
    return queryClient.invalidateQueries(['source_record_type', sourceRecordTypeId]);
}

export const useSourceDataConnection = (connectionId: string) => {
    return useQuery(['source_data_connection', connectionId], async () => {
        if (connectionId) {
            return await SourceDataConnectionORM.findById(connectionId);

        }
        return null;
    }, {
        staleTime: 10 * 1000
    });
    
}

export const useSourceDataConnections = (sourceRecordTypeId: string = '', relationshipId: string = '') => {
    return useQuery(['source_data_connections', sourceRecordTypeId, relationshipId], async () => {
        if (!sourceRecordTypeId && !relationshipId) {
            return [];
        } else if (sourceRecordTypeId) {
            const { records } = await ApiService.getInstance().request('GET', `/sourcerecordtypes/${sourceRecordTypeId}/connections`) as ListRecordsResponse<SourceDataConnection>;
            return records;
        } else {
            const { records } = await ApiService.getInstance().request('GET', `/businessobjectrelationships/${relationshipId}/connections`) as ListRecordsResponse<SourceDataConnection>;
            return records;
        }
        
        
    }, {
        staleTime: 30 * 1000,  // 30 seconds stale time
    });
}

export const usePipelines = () => {
    return useQuery('pipelines', async () => {
        const { records } = await PipelineORM.find();
        return records;
    }, {
        staleTime: 30 * 1000 // 30 seconds stale time
    });
}


export const usePipelineOrchestration = (pipelineOrchestrationId: string | null, refetchInterval: (data: any | undefined, query: any) => any) =>
    useQuery<PipelineOrchestration>({ queryKey: ['pipeline-orchestration', pipelineOrchestrationId], refetchInterval, queryFn: () => PipelineORM.getPipelineOrchestration(pipelineOrchestrationId as string), enabled: !!pipelineOrchestrationId});

export const useBuildOrchestration = (buildOrchestrationId: string | null, refetchInterval: (data: any | undefined, query: any) => any) =>
    useQuery<BuildOrchestration>({ queryKey: ['build-orchestration', buildOrchestrationId], refetchInterval, queryFn: () => BuildOrchestrationORM.findById(buildOrchestrationId as string), enabled: !!buildOrchestrationId});

export const useBuildOrchestrationHistory = () => {
    return useQuery<BuildOrchestration[]>({ queryKey: ['build-orchestrations', 'history'], queryFn: () => BuildOrchestrationORM.getHistory()})
}

export const useSourceTypes = (enabled: boolean = true) =>
    useQuery<SourceType[]>({ queryKey: ['enabledSourceTypes'], queryFn: () => SourceORM.getEnabledSourceTypes(), enabled });


export const useFiles = (sourceId: string = '') => {
    return useQuery(['files', sourceId], async () => {
        let filter = {}
        if (!!sourceId) {
            filter = {'source_id': sourceId}
        }
        const { records } = await FileORM.find(filter);
        return records;
    }, {
        staleTime: 30 * 1000,  // 30 seconds stale time
    });
}

export const reloadFiles = () => {
    queryClient.invalidateQueries('files');
}

export const useDataMarts = () => {
    return useQuery('datamarts', async () => {
        const { records } = await DataMartORM.find();
        return records;
    }, {
        staleTime: 30 * 1000 // 30 seconds stale time
    });
}

export const invalidateDataMarts = () => {
    queryClient.invalidateQueries('datamarts');
}

export const useDataMart = (dataMartId: string) => {
    return useQuery(['datamarts', dataMartId], async () => {
        if (dataMartId) {
            return DataMartORM.findById(dataMartId);
        }
        return null;
    }, {
        staleTime: 30 * 1000,
    });
}

export const saveDataMart = async (dm: DataMart) => {
    const result = await DataMartORM.save(dm);
    queryClient.invalidateQueries('datamarts');
    return result;
}

export const deleteDataMart = async (id: string) => {
    const result = await DataMartORM.deleteById(id);
    queryClient.invalidateQueries('datamarts');
    return result;
}

export const useContextItems = () => {
    return useQuery('context_items', async () => {
        const { records } = await ContextORM.find();
        return records;
    }, {
        staleTime: 60 * 1000,
    });
};

export const saveContextItem = async (contextItem: ContextItem) => {
    const result = await ContextORM.save(contextItem);
    queryClient.invalidateQueries('context_items');
    return result;
};

export const deleteContextItem = async (contextItem: ContextItem) => {
    const result = await ContextORM.delete(contextItem);
    queryClient.invalidateQueries('context_items');
    return result;
};

export interface MissionControlDataSourceNode {
    id: string;
    source_id: string;
    source_record_type_id: string;
    label: string;
    icon_path: string;
    attributes: ColumnPreference[];
    side: string;
}



interface MissionControlDataSourceEdge {
    id: string;
    source: string;
    source_attribute: string;
    target: string;
    target_attribute: string;
}

interface MissionControlDataSourceResponse {
    nodes: MissionControlDataSourceNode[];
    edges: MissionControlDataSourceEdge[];
}


export const useMissionControlDataSourcesData = (relationshipId: string) => {
    return useQuery(['mission_control_data_sources', relationshipId], async () => {
        if (!relationshipId) {
            return {
                nodes: [],
                edges: [],
            }
        }
        const result = await ApiService.getInstance().request('GET', `/businessobjectrelationships/${relationshipId}/graph`) as MissionControlDataSourceResponse;
        return result;
    }, {});
}

export const invalidateMissionControlDataSourcesData = () => {
    queryClient.invalidateQueries('mission_control_data_sources');
}



export interface MissionControlDataFlowNode {
    id: string;
    data: MissionControlNodeProps,
}


export interface MissionControlDataFlowEdge {
    id: string;
    source: string;
    target: string;
    data: {
        type: 'NODE_RELATIONSHIP' | 'MAPPING' | 'REPORT';
        object_id: string;
        secondary_id?: string;
        status_indicator?: string;
    };
}
export interface MissionControlDataResponse {
    nodes: MissionControlDataFlowNode[];
    edges: MissionControlDataFlowEdge[];
    is_stale: boolean;
}

export const useMissionControlDataFlowData = () => {
    return useQuery('mission_control_data_flow', async () => {
        const result = await ApiService.getInstance().request('GET', '/mission_control/data-flow') as MissionControlDataResponse;
        return result;
    }, {
        staleTime: 1000,
    });
}

export const invalidateMissionControlDataFlowData = () => {
    queryClient.invalidateQueries('mission_control_data_flow');
}


export const useDataLoadsForSource = (sourceId: string) => {
    return useQuery(['dataloads', sourceId], async () => {
        if (!sourceId) {
            return [];
        }
        const result = await DataLoadORM.find({
            'source_id': sourceId
        }, 10, 1, '-started_at');

        return result.records;
    }, {
        staleTime: 30000
    });
}


export const invalidateDataLoadsForSRT = (srtId: string) => {
    queryClient.invalidateQueries(['dataloads', srtId]);
}

interface AmbiguousRecordsResponse {
    child_data: {
        [key: string]: any
    };
    parent_data: {
        [key: string]: any
    }[];
    child_plb_uuid: string;
    group_id: string;
}

export const useExampleAmbiguousRecordsForRelationship = (relationshipId: string) => {
    return useQuery(['relationshipAmbiguousRecords', relationshipId], async () => {
        const result = await ApiService.getInstance().request('GET', `/pipelinenoderelationships/${relationshipId}/example-ambiguous`) as ListRecordsResponse<AmbiguousRecordsResponse>
        return result.records
    }, {
        staleTime: 5 * 60 * 1000,
    })
}

export const useExampleOrphanRecordsForRelationship = (relationshipId: string) => {
    return useQuery(['relationshipOrphanRecords', relationshipId], async () => {
        const result = await ApiService.getInstance().request('GET', `/pipelinenoderelationships/${relationshipId}/example-orphans`) as ListRecordsResponse<any>
        return result.records
    }, {
        staleTime: 5 * 60 * 1000,
    })
}

export const invalidateRelationshipIssueData = () => {
    queryClient.invalidateQueries('relationshipOrphanRecords');
    queryClient.invalidateQueries('relationshipAmbiguousRecords');
}


export const useBusinessObjectData = (
    businessObjectId: string,
    sortColumn: string,
    page: number = 1,
    searchQuery: string = '',
    additionalWheres: string = '',
) => {
    return useQuery([
        'business_object_data', 
        businessObjectId,
        sortColumn,
        page,
        searchQuery,
        additionalWheres,
    ], async () => {
        const result = await ApiService.getInstance().request('GET', `/businessobjects/${businessObjectId}/data`, {
            limit: 100,
            page: page,
            sort: sortColumn,
            q: searchQuery,
            wheres: additionalWheres,
        }) as TableLoadResponse;
        return result;
    }, {
        staleTime: 60 * 1000 * 30
    })
}

export const invalidateAfterBuild = () => {
    queryClient.invalidateQueries('business_object_data');
    queryClient.invalidateQueries('relationshipAmbiguousRecords')
    queryClient.invalidateQueries('relationshipOrphanRecords')
    queryClient.invalidateQueries('report_columns')
    queryClient.invalidateQueries('business_object_relationship')
    queryClient.invalidateQueries('business_object_relationships');
    invalidateMissionControlDataFlowData();
}

export const useSourceData = (
    sourceRecordTypeId: string,
    sortColumn: string,
    page: number = 1,
    searchQuery: string = '',
    additionalWheres: string = '',
) => {
    return useQuery([
        'source_data', 
        sourceRecordTypeId,
        sortColumn,
        page,
        searchQuery,
        additionalWheres,
    ], async () => {
        const result = await ApiService.getInstance().request('GET', `/sourcerecordtypes/${sourceRecordTypeId}/data`, {
            limit: 100,
            page: page,
            sort: sortColumn,
            q: searchQuery,
            wheres: additionalWheres,
        }) as TableLoadResponse;
        return result;
    }, {
        staleTime: 60 * 1000 * 30
    })
}

export const useJoinPaths = (pipelineNodeId: string) => {
    return useQuery(['join_paths', pipelineNodeId], async () => {
        if (!pipelineNodeId) {
            return [];
        }

        const result = await ApiService.getInstance().request('GET', `/pipelinenodes/${pipelineNodeId}/join-paths`) as ListRecordsResponse<NodeFieldJoinPath[]>;
        return result.records;
    })
}


export const useReportColumns = (pipelineNodeId: string) => {
    return useQuery(['report_columns', pipelineNodeId], async () => {
        if (!pipelineNodeId) {
            return [];
        }
        const result = await ApiService.getInstance().request('GET', `/pipelinenodes/${pipelineNodeId}/reporting-columns`) as ListRecordsResponse<ReportColumn>;
        return result.records;
    }, {
        staleTime: 60000,
    });
}



export const useSourceTypeConfig = (sourceTypeId: string) => {
    return useQuery(['source_type_config', sourceTypeId], async () => {
        if (!sourceTypeId) {
            return null;
        }

        const result = await ApiService.getInstance().request('GET', `/sources/source-type/${sourceTypeId}/config`) as SingleRecordResponse<SourceType>;
        return result.record;
    }, {
        staleTime: 60000
    })
}

export const useSourceSetupScript = (sourceId: string) => {
    return useQuery(['source_setup_script', sourceId], async () => {
        if (!sourceId) {
            return null;
        }

        const result = await ApiService.getInstance().request('GET', `/sources/${sourceId}/setup-script`) as SingleRecordResponse<string>;
        return result.record;
    }, {
        staleTime: 60000
    })
}

export const useSourceStreams = (sourceId: string, enabled: boolean = true) => {
    return useQuery(['source_streams', sourceId], async () => {
        if (!sourceId) {
            return [];
        }
        const result = await ApiService.getInstance().request('GET', `/sources/${sourceId}/streams`) as { streams: string[] };
        return result.streams;
    }, {
        staleTime: 60 * 1000,
        enabled: enabled && !!sourceId
    });
}

export const useTemplates = () => {
    return useQuery('templates', async () => {
        const result = await ApiService.getInstance().request('GET', '/templates/all') as ListRecordsResponse<Template>;
        return result.records;
    }, {
        staleTime: 60000,
    })
}

export const useTemplateGroup = (templateGroupId: string) => {
    return useQuery(['template_group_id', templateGroupId], async () => {
        return await TemplateGroupORM.findById(templateGroupId);
    });
}

export const deleteTemplateGroup  = async (templateGroup: TemplateGroup) => {
    const result = await TemplateGroupORM.delete(templateGroup);
    queryClient.invalidateQueries(['template_group_id', templateGroup.id]);
    return result;
}

export const invalidateEverything = () => {
    return queryClient.invalidateQueries();
}

export interface ColumnValueDistribution {
    value: string;
    total: number;
}

export const useColumnValueDistribution = (pipelineNodeId: string, columnId: string, limit: number = 10) => {
    return useQuery(['column_value_distribution', pipelineNodeId, columnId, limit],  async () => {
        if (!pipelineNodeId || !columnId) {
            return [];
        }
        const result = await ApiService.getInstance().request('GET', `/pipelinenodes/${pipelineNodeId}/column/${columnId}/distro`, {
            limit: limit,
        }) as ListRecordsResponse<ColumnValueDistribution>;
        return result.records;
    }, {
        staleTime: 30000,
    })
}

export const useColumnShape = (pipelineNodeId: string, columnId: string) => {
    return useQuery(['column_shape', pipelineNodeId, columnId],  async () => {
        const result = await ApiService.getInstance().request('GET', `/pipelinenodes/${pipelineNodeId}/column/${columnId}/shape`) as SingleRecordResponse<ColumnStats>;
        
        return result.record;
    }, {
        staleTime: 30000,
    });
}

export const useDrilldown = (dataMartId: string, metricIdx: number, breakdownValues: string[]) => {
    return useQuery(['drilldown', dataMartId, metricIdx, breakdownValues], async () => {
        const result = await ApiService.getInstance().request('POST', `/datamarts/${dataMartId}/drilldown`, {
            drilldown_metric_idx: metricIdx,
            breakdown_values: breakdownValues,
        }) as ListRecordsResponse<any>;
        return result.records;
    }, {
        staleTime: 30000
    })
}

export const invalidateDrilldowns = (dataMartId: string) => {
    queryClient.invalidateQueries(['drilldown', dataMartId]);

}

export const usePipelineNodes = (filter: any = null) => {
    return useQuery(['pipeline_nodes', filter ? JSON.stringify(filter) : null], async () => {
        const { records } = await PipelineNodeORM.find(filter, 500);
        return records;
    }, {
        staleTime: Infinity,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });
}

export const useDateDimensionNode = () => {
    return useQuery(['pipeline_nodes', 'date_dimension'], async () => {
        const { records } = await PipelineNodeORM.find({
            node_type: 'DATE_DIMENSION',
        }, 1);

        if (records.length == 0) {
            return null;
        }   
        return records[0];
    }, {
        staleTime: Infinity,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });
}

export const usePipelineNode = (pipelineNodeId?: string, enabled: boolean = true) => {
    return useQuery(['pipeline_nodes', pipelineNodeId || 'undefined'], async () => {
        if (!pipelineNodeId) {
            return null;
        }
        return await PipelineNodeORM.findById(pipelineNodeId);
    }, {
        enabled,
        retry: 0,

        refetchOnMount: true,
        // Don't refetch window focus
        refetchOnWindowFocus: false,
        staleTime: Infinity,
    });
}

export const useFile = (fileId?: string) => {
    return useQuery(['files', fileId], async () => {
        if (!fileId) {
            return null;
        }
        return await FileORM.findById(fileId);
    }, {
        retry: 0,
        staleTime: 30 * 1000 // 30 seconds stale time
    });
}

export const useOutputTableForNode = (pipelineNodeId?: string) => {
    return useQuery(['pipeline_nodes', pipelineNodeId, 'output_table'], async () => {
        if (!pipelineNodeId) {
            return '';
        }
        const resp = await ApiService.getInstance().request('GET', `/pipelinenodes/${pipelineNodeId}/output`) as SingleRecordResponse<any>;
        return resp.record.output_table;
    });
}

export const savePipelineNode = async (node: PipelineNode) => {
    const result = await PipelineNodeORM.save(node);
    invalidatePipelineNodes();
    invalidatePipelineNode(node.id as string);
    invalidateMissionControlDataFlowData();
    return result;
};

export const savePipelineNodeRelationship = async (rel: PipelineNodeRelationship) => {
    const result = await PipelineNodeRelationshipORM.save(rel);
    invalidatePipelineNodeRelationships();
    invalidateMissionControlDataFlowData();
    return result;
};


export const usePipelineNodeRelationshipPeers = (relationshipId: string) => {
    return useQuery(['pipeline_node_relationships', relationshipId, 'peers'], async () => {
        if (!relationshipId) {
            return [];
        }

        const response = await ApiService.getInstance().request('GET', `/pipelinenoderelationships/${relationshipId}/peers`) as ListRecordsResponse<PipelineNodeRelationship>;
        return response.records;

    }, {
        staleTime: 30 * 1000
    })
}

export const invalidatePipelineNode = (pipelineNodeId: string) => {
    return queryClient.invalidateQueries(['pipeline_nodes', pipelineNodeId]);
}

export const invalidatePipelineNodes = () => {
    return queryClient.invalidateQueries('pipeline_nodes');
}

export const invalidatePipelineNodeRelationships = () => {
    return queryClient.invalidateQueries('pipeline_node_relationships');
}

export const usePipelineNodeRelationship = (relationshipId: string) => {
    return useQuery(['pipeline_node_relationships', relationshipId], async () => {
        if (!relationshipId) {
            return undefined;
        }
        const result = await PipelineNodeRelationshipORM.findById(relationshipId);
        return result;
    }, {
        staleTime: 30000,
    });
}

export const usePipelineNodeRelationships = (pipelineNodeId?: string) => {
    return useQuery(['pipeline_node_relationships', pipelineNodeId], async () => {
        if (!!pipelineNodeId) {
            const results = await PipelineNodeRelationshipORM.find({
                '$or': [
                    {
                        'child_node_id': pipelineNodeId
                    }, {
                        'parent_node_id': pipelineNodeId
                    }
                ]
            });
            return results.records;

        } else {
            const results = await PipelineNodeRelationshipORM.find();
            return results.records;
        }
        
    }, {
        staleTime: Infinity,
        refetchOnMount: false,
    });
}


export const setViewportInfo = (viewport:Viewport) => {
    queryClient.setQueryData(['viewport'], viewport);
}

export const getViewportInfo = () : Viewport | undefined => {
    return queryClient.getQueryData(['viewport']);
}

export interface SnowflakeColumnOption {
    name: string;
    type: string;
    primary_key: string;
}

export const useSourceColumns = (pipelineNodeId: string) => {
    return useQuery(['pipeline_node_source_columns', pipelineNodeId], async () => {
        if (!pipelineNodeId) {
            return [];
        }
        const results = await ApiService.getInstance().request('GET', `/pipelinenodes/${pipelineNodeId}/source-columns`) as ListRecordsResponse<SnowflakeColumnOption>;
        return results.records;
    }, {
        // 5 mins
        staleTime: 60 * 1000 * 5
    });
}

export const useAvailableBranches = () => {
    return useQuery(['github_branches'], async () => {
        const results = await ApiService.getInstance().request('GET', '/github/branches') as ListRecordsResponse<any>;
        return results.records;
    }, {
        staleTime: 30 * 1000
    });
}

export const getTableData = async (
    tablePath: string,
    page: number,
    limit: number,
    filters?: string,
    cacheBuster?: string,
    searchQuery?: string,
    sortBy?: string,
    whitelist?: string,
) => {
    if (!tablePath) {
        return {
            records: [],
            total_pages: 0,
            total_records: 0,
        }
    }
    const tableParams = {
        q: searchQuery,
        page: page,
        limit: limit,
        sort: sortBy,
        filters: filters,
        cacheBuster: cacheBuster,
        whitelist: whitelist,
    }


    const url = apiurl(tablePath, tableParams);

    const listRecordsResp = await ApiService.getInstance().request('GET', url) as TableLoadResponse;
    return listRecordsResp;
}

export const useTableData = (
    tablePath: string,
    page: number,
    limit: number,
    filters?: string,
    cacheBuster?: string,
    searchQuery?: string,
    sortBy?: string,
    whitelist?: string,
) => {
    return useQuery(['table_data', tablePath, searchQuery, sortBy, page, limit, filters, cacheBuster, whitelist], async () => {
        return getTableData(tablePath, page, limit, filters, cacheBuster, searchQuery, sortBy, whitelist);
    }, {
        staleTime: 60 * 1000 * 5,
        retry: 0,
    });
}

export const invalidateTableData = () => {
    queryClient.invalidateQueries('table_data');
}

export const toggleThenaWidget = () => {
    const thenaIframeEle = window.document.getElementById(
        'thena-iframe-element'
    ) as HTMLIFrameElement | null;
    thenaIframeEle?.contentDocument?.dispatchEvent(
        new CustomEvent('TOGGLE_THENA_WIDGET')
    );
}

// Chat history toggle event
export const CHAT_HISTORY_TOGGLE_EVENT = 'TOGGLE_CHAT_HISTORY';

export const toggleChatHistory = () => {
    window.dispatchEvent(new CustomEvent(CHAT_HISTORY_TOGGLE_EVENT));
}

export const useChatHistoryToggle = (callback: () => void) => {
    useEffect(() => {
        const handler = () => callback();
        window.addEventListener(CHAT_HISTORY_TOGGLE_EVENT, handler);
        return () => window.removeEventListener(CHAT_HISTORY_TOGGLE_EVENT, handler);
    }, [callback]);
}


interface GraphDataResponse {
    data: {
        nodes: {
            id: string;
            title: string;
        }[];
        edges: {
            from_id: string;
            to_id: string;
            relationship_id: string;
            id: string;
        }[]
    }
}


export const useReportingTree = () => {
    return useQuery(['reporting-tree'], async () => {
        const response = await ApiService.getInstance().request('GET', '/pipelinenodes/reporting-tree') as GraphDataResponse;
        return response.data;
    })
}

export interface InstallationInfo {
  next_version: number;
  count_at_next: number;
  count_below_next: number;
  count_no_version: number;
  total_installations: number;
}

export interface InstallationPlan {
  installation_info: InstallationInfo;
}

export const useInstallationInfo = (templateId: string) => {
    return useQuery(['installation_info', templateId], async () => {
        const response = await ApiService.getInstance().request('GET', `/templates/upgrade-all-installations/${templateId}/plan`) as SingleRecordResponse<InstallationPlan>;
        return response.record;
    });
}

export const useIsSupersetEnabled = () => {
    const supersetConfigEnabled = !!ConfigService.getInstance().config?.superset;
    const hostedSupersetEnabled = useFeatureFlagValue('hosted-superset', false);
    return supersetConfigEnabled && hostedSupersetEnabled;
}


/**
 * Right now this is the way to trigger the job to go through each measure
 * and make sure its join paths are set correctly.
 */
export const processMeasures = async () => {
    await ApiService.getInstance().request('POST', '/mission_control/process-measures');
}
export const useBillingOptions = () => {
    return useQuery('billingOptions', async () => {
        return BillingAccountORM.getBillingOptions();
    });
}

export const useBillingAccount = (forceCheck: boolean = false) => {
    return useQuery(['billing_account', forceCheck], async () => {
        return BillingAccountORM.getBillingAccount(forceCheck);
    });
}

export const toggleFavoriteNode = async (nodeId: string) => {
    const currentConfig = ConfigService.getInstance().userConfig;

    if (!currentConfig) {
        return;
    }
    const favorites = currentConfig.favorite_node_ids || [];
    
    const newFavorites = favorites.includes(nodeId) 
        ? favorites.filter(id => id !== nodeId)
        : [...favorites, nodeId];
    
    await saveUserConfig({
        ...currentConfig,
        favorite_node_ids: newFavorites
    });
    
    queryClient.invalidateQueries('user_config');
}

export const toggleFavoriteAnalysis = async (analysisId: string) => {
    const currentConfig = ConfigService.getInstance().userConfig;
    if (!currentConfig) {
        return;
    }
    const favorites = currentConfig.favorite_analysis_ids || [];
    
    const newFavorites = favorites.includes(analysisId)
        ? favorites.filter(id => id !== analysisId)
        : [...favorites, analysisId];
    
    await saveUserConfig({
        ...currentConfig,
        favorite_analysis_ids: newFavorites
    });
    
    queryClient.invalidateQueries('user_config');
}

export const toggleFavoriteDashboard = async (dashboardId: string) => {
    const currentConfig = ConfigService.getInstance().userConfig;
    if (!currentConfig) {
        return;
    }
    const favorites = currentConfig.favorite_dashboard_ids || [];
    
    const newFavorites = favorites.includes(dashboardId)
        ? favorites.filter(id => id !== dashboardId)
        : [...favorites, dashboardId];
    
    await saveUserConfig({
        ...currentConfig,
        favorite_dashboard_ids: newFavorites
    });
    
    queryClient.invalidateQueries('user_config');
}

export const useIsFavoriteNode = (nodeId: string) => {
    const { data: userConfig } = useUserConfig();
    return userConfig?.favorite_node_ids?.includes(nodeId) || false;
}

export const useIsFavoriteAnalysis = (analysisId: string) => {
    const { data: userConfig } = useUserConfig();
    return userConfig?.favorite_analysis_ids?.includes(analysisId) || false;
}

export const useIsFavoriteDashboard = (dashboardId: string) => {
    const { data: userConfig } = useUserConfig();
    return userConfig?.favorite_dashboard_ids?.includes(dashboardId) || false;
}

export const useFavoriteNodes = () => {
    const { data: userConfig } = useUserConfig();
    const favoriteIds = userConfig?.favorite_node_ids || [];
    const { data: allNodes } = usePipelineNodes();
    
    return useMemo(() => {
        if (!allNodes || !favoriteIds.length) return [];
        return allNodes.filter(node => favoriteIds.includes(node.id as string));
    }, [allNodes, favoriteIds]);
}

export const useFavoriteAnalyses = () => {
    const { data: userConfig } = useUserConfig();
    const favoriteIds = userConfig?.favorite_analysis_ids || [];
    const { data: allAnalyses } = useAnalyses();
    
    return useMemo(() => {
        if (!allAnalyses || !favoriteIds.length) return [];
        return allAnalyses.filter(analysis => favoriteIds.includes(analysis.id as string));
    }, [allAnalyses, favoriteIds]);
}

export const useFavoriteDashboards = () => {
    const { data: userConfig } = useUserConfig();
    const favoriteIds = userConfig?.favorite_dashboard_ids || [];
    const { data: allDashboards } = useDashboards();
    
    return useMemo(() => {
        if (!allDashboards || !favoriteIds.length) return [];
        return allDashboards.filter(dashboard => favoriteIds.includes(dashboard.id as string));
    }, [allDashboards, favoriteIds]);
}

export const useDashboardNotifications = (dashboardId: string) => {
    return useQuery(['dashboard_notiifcations', dashboardId], async () => {
        if (!dashboardId) {
            return undefined;
        }
        const record = await DashboardORM.getNotifications(dashboardId);
        return record;
    });
}

export interface ChatThread {
    thread_id: string;
    name: string;
    agent_type: string;
    created_by: string;
    created_at: string;
    updated_at: string;
    shared_with: string[];
    is_owner: boolean;
}

export const useChatThreads = (agentLabel?: string, limit: number = 50) => {
    return useQuery(['chat_threads', agentLabel, limit], async () => {
        const params: Record<string, any> = { limit };
        if (agentLabel) {
            params.agent_label = agentLabel;
        }
        const result = await ApiService.getInstance().request('GET', '/chat/threads', params) as { threads: ChatThread[] };
        return result.threads;
    }, {
        staleTime: 30 * 1000,
    });
}

export const invalidateChatThreads = () => {
    queryClient.invalidateQueries(['chat_threads']);
}

export interface ChatThreadMessage {
    id: string;
    role: 'user' | 'assistant' | 'tool';
    content: string;
    tool_calls?: any[];
    tool_call_id?: string;
    name?: string;
}

export interface ChatThreadMessagesResult {
    messages: ChatThreadMessage[];
}

export const getChatThreadMessages = async (threadId: string): Promise<ChatThreadMessagesResult> => {
    const result = await ApiService.getInstance().request('GET', `/chat/threads/${threadId}/messages`) as { thread_id: string; messages: ChatThreadMessage[] };
    return {
        messages: result.messages,
    };
}

export const getChatThreads = async (agentLabel?: string, limit: number = 50): Promise<ChatThread[]> => {
    const params: Record<string, any> = { limit };
    if (agentLabel) {
        params.agent_label = agentLabel;
    }
    const result = await ApiService.getInstance().request('GET', '/chat/threads', params) as { threads: ChatThread[] };
    return result.threads;
}

export const deleteChatThread = async (threadId: string): Promise<void> => {
    await ApiService.getInstance().request('DELETE', `/chat/threads/${threadId}`);
    invalidateChatThreads();
}

export const updateChatThreadName = async (threadId: string, name: string): Promise<void> => {
    await ApiService.getInstance().request('PATCH', `/chat/threads/${threadId}`, { name });
    invalidateChatThreads();
}
